import { AssignmentOutlined, InfoOutlined } from '@mui/icons-material';
import { React, useState, Dialog, DialogActions, CloseIcon, DialogContent, DialogTitle, Divider, IconButton, Grid2, Typography, Box, useEffect } from '../../../utils/globalImports';
import { CustomRadioButton, CustomButton } from '../../../utils/globalImports';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: () => void;  // Function to handle modal close
    frameworks: Framework[];
    selectedFrameworkId: number;
    onChange: (id: number) => void;
    onSubmit: () => void;
}

interface Framework {
    frameworkId: number;
    frameworkName: string;
    // description?: string; 
    isDefault?: boolean;  
}

const ProposalFrameworkModal: React.FC<ModalProps> = ({
    open,
    onClose,
    frameworks,
    selectedFrameworkId,
    onChange,
    onSubmit
}) => {

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)'
                }
            }}
        >
            {/* Modal Title */}
            <DialogTitle
                sx={{
                    position: 'relative',
                    color: '#1e66ab', // Dark navy blue from image
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '28px',
                    py: 3
                }}
            >
                Select Framework

                <IconButton
                    color="inherit"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 24,
                        top: 24,
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '6px',
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: '#f8fafc'
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: '20px' }} />
                </IconButton>
            </DialogTitle>

                <Divider></Divider>

            <DialogContent sx={{ px: 4, py: 3 }}>
                <Box sx={{ flexGrow: 1 }}>

                    {/* Header Choose Framework Box */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                        {/* Circular Icon Wrapper */}
                        <Box
                            sx={{
                                backgroundColor: '#f7faff',
                                borderRadius: '50%',
                                width: '48px',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <AssignmentOutlined sx={{color: '#1e66ab'}}/>
                        </Box>

                        <Box>
                            <Typography sx={{ fontSize: "18px", color: "#1e66ab", fontWeight: 600 }}>
                                Choose a framework
                            </Typography>
                            <Typography sx={{ fontSize: "14px", color: "#64748b", mt: 0.5 }}>
                                Select the framework you want to use for adding fee stages.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Stacked Framework Option List */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        {frameworks.map((fw) => {
                            const isChecked = selectedFrameworkId === fw.frameworkId;
                            return (
                                <Box
                                    key={fw.frameworkId}
                                    onClick={() => onChange(fw.frameworkId)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        border: isChecked ? '2px solid #1e66ab' : '1px solid #e2e8f0',
                                        backgroundColor: isChecked ? '#f7faff' : '#ffffff',
                                        borderRadius: '12px',
                                        p: '20px 24px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            borderColor: '#1e66ab'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <CustomRadioButton
                                            value={fw.frameworkId}
                                            checked={isChecked}
                                            onChange={(event) => {
                                                event.stopPropagation();
                                                onChange(Number(event.target.value));
                                            }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, color: '#1e66ab', fontSize: '16px' }}>
                                                {fw.frameworkName}
                                            </Typography>
                                            {/* {fw.description ? (
                                                <Typography sx={{ color: '#64748b', fontSize: '14px', mt: 0.5 }}>
                                                    {fw.description}
                                                </Typography>
                                            ) : null} */}
                                        </Box>
                                    </Box>

                                    {/* Safe check for rendering the "Default" badge without leaving a "0" layout residue */}
                                    {fw.isDefault ? (
                                        <Box
                                            sx={{
                                                backgroundColor: '#eef4fc',
                                                color: '#1e66ab',
                                                px: 2,
                                                py: 0.5,
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                fontWeight: 600
                                            }}
                                        >
                                            Default
                                        </Box>
                                    ) : null}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Blue Warning Banner */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            backgroundColor: '#f7faff',
                            border: '1px solid #e2edfc',
                            borderRadius: '8px',
                            p: 2
                        }}
                    >
                        {/* Inline Info Badge SVG */}
                       <InfoOutlined sx={{color: '#1e66ab'}}/>
                        <Typography sx={{ color: '#1e66ab', fontSize: '14px', fontWeight: 500 }}>
                            You can only select one framework at a time.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <Divider sx={{ borderColor: '#f1f5f9', mt: 1 }} />

            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1.5,
                                }}
                            >
                                <CustomButton
                                    capsule
                                    onClick={onClose}
                                    sx={{
                                        textTransform: 'none',
                                        width: 'auto',
                                        minWidth: 'fit-content',
                                        flex: 'none',
                                    }}
                                >
                                    Cancel
                                </CustomButton>
            
                                <CustomButton
                                    capsule
                                    onClick={onSubmit}
                                    sx={{
                                        textTransform: 'none',
                                        width: 'auto',
                                        minWidth: 'fit-content',
                                        flex: 'none',
                                    }}
                                >
                                    Add Fee Stage
                                </CustomButton>
                            </DialogActions>
        </Dialog>
    );
}

export default ProposalFrameworkModal;